import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Users, RefreshCw, Pencil, Trash2, Search, Loader2, Filter, ShieldAlert, Building2, UserPlus, Copy, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../lib/apiBase';

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const jheaders = () => ({ 'Content-Type': 'application/json', ...headers() });

const SENTINEL = '__keep__'; // means "no change" in bulk-edit selects

export default function MasterDirectory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ departments: [], designations: [], categories: [], appointment_types: [], roles: [], entities: [] });

  // Filters
  const [search, setSearch] = useState('');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [filterDesignation, setFilterDesignation] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterApptType, setFilterApptType] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Edit dialog
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    department: '', designation: '', category: 'Teaching',
    appointment_type: 'Permanent', entity_id: '', role: 'employee',
    password: '',
  });
  const [addResult, setAddResult] = useState(null);

  // Bulk dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    entity_id: SENTINEL, department: SENTINEL, designation: SENTINEL, category: SENTINEL,
    appointment_type: SENTINEL, role: SENTINEL, is_active: SENTINEL,
  });

  // Delete dialog
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteHard, setDeleteHard] = useState(false);
  const [deleteHistory, setDeleteHistory] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Bulk delete dialog
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteHard, setBulkDeleteHard] = useState(false);
  const [bulkDeleteHistory, setBulkDeleteHistory] = useState(false);

  // Password reset (works for both single and bulk — the confirm dialog just
  // hangs onto the target employee_ids for the caller to confirm before firing).
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTargets, setResetTargets] = useState([]); // [{employee_id, name, email}]
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null); // {credentials: [{email, password, ...}], ...} for single-inline flow

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search) qs.set('search', search);
      if (filterEntity !== 'all') qs.set('entity_id', filterEntity);
      if (filterDept !== 'all') qs.set('department', filterDept);
      if (filterDesignation !== 'all') qs.set('designation', filterDesignation);
      if (filterCategory !== 'all') qs.set('category', filterCategory);
      if (filterApptType !== 'all') qs.set('appointment_type', filterApptType);
      if (filterRole !== 'all') qs.set('role', filterRole);
      if (filterStatus !== 'all') qs.set('status', filterStatus);
      const res = await fetch(`${API}/api/admin/master-directory?${qs.toString()}`, { headers: headers() });
      if (!res.ok) throw new Error('Load failed');
      const data = await res.json();
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load directory');
    } finally {
      setLoading(false);
    }
  }, [search, filterEntity, filterDept, filterDesignation, filterCategory, filterApptType, filterRole, filterStatus]);

  const fetchMeta = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/master-directory/meta`, { headers: headers() });
      if (res.ok) setMeta(await res.json());
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.employee_id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.employee_id)));
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      email: row.email || '',
      phone: row.phone || '',
      department: row.department || '',
      designation: row.designation || '',
      category: row.category || '',
      appointment_type: row.appointment_type || '',
      entity_id: row.entity_id || '',
      role: row.role || 'employee',
      is_active: row.is_active !== false,
    });
  };

  const submitEdit = async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/master-directory/${editRow.employee_id}`, {
        method: 'PUT', headers: jheaders(), body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success('Employee updated everywhere');
      setEditRow(null);
      await fetchRows();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setAddResult(null);
    setAddForm({
      first_name: '', last_name: '', email: '', phone: '',
      department: '', designation: '', category: 'Teaching',
      appointment_type: 'Permanent', entity_id: '', role: 'employee',
      password: '',
    });
    setAddOpen(true);
  };

  const submitAdd = async () => {
    if (!addForm.first_name.trim() || !addForm.email.trim()) {
      toast.error('First name and email are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...addForm, entity_id: addForm.entity_id || undefined };
      const res = await fetch(`${API}/api/admin/master-directory`, {
        method: 'POST', headers: jheaders(), body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Create failed');
      setAddResult(data);
      toast.success('Employee created');
      await fetchRows();
    } catch (e) {
      toast.error(e.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const copyAddCreds = () => {
    if (!addResult) return;
    const txt = `Login: ${window.location.origin}/login\nEmail: ${addResult.email}\nPassword: ${addResult.temp_password}`;
    navigator.clipboard?.writeText(txt);
    toast.success('Credentials copied');
  };

  const submitBulk = async () => {
    const changes = {};
    Object.entries(bulkForm).forEach(([k, v]) => {
      if (v !== SENTINEL && v !== '' && v !== undefined && v !== null) {
        changes[k] = (k === 'is_active') ? (v === 'true') : v;
      }
    });
    if (Object.keys(changes).length === 0) { toast.error('Pick at least one field to change'); return; }
    if (selected.size === 0) { toast.error('Select rows first'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/master-directory/bulk-update`, {
        method: 'POST', headers: jheaders(),
        body: JSON.stringify({ employee_ids: Array.from(selected), changes }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Bulk update failed');
      toast.success(`Updated ${data.updated} employees`);
      setBulkOpen(false);
      setSelected(new Set());
      await fetchRows();
    } catch (e) {
      toast.error(e.message || 'Bulk update failed');
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteRow) return;
    setDeleting(true);
    try {
      const qs = new URLSearchParams();
      if (deleteHard) qs.set('hard', 'true');
      if (deleteHard && deleteHistory) qs.set('delete_history', 'true');
      const res = await fetch(`${API}/api/admin/master-directory/${deleteRow.employee_id}?${qs.toString()}`, {
        method: 'DELETE', headers: headers(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Delete failed');
      toast.success(deleteHard ? 'Employee permanently deleted' : 'Employee deactivated');
      setDeleteRow(null);
      setDeleteHard(false); setDeleteHistory(false);
      await fetchRows();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const submitBulkDelete = async () => {
    if (selected.size === 0) { toast.error('Select rows first'); return; }
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/admin/master-directory/bulk-delete`, {
        method: 'POST', headers: jheaders(),
        body: JSON.stringify({ employee_ids: Array.from(selected), hard: bulkDeleteHard, delete_history: bulkDeleteHard && bulkDeleteHistory }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Bulk delete failed');
      toast.success(`${bulkDeleteHard ? 'Deleted' : 'Deactivated'} ${data.count} employees`);
      setBulkDeleteOpen(false);
      setBulkDeleteHard(false); setBulkDeleteHistory(false);
      setSelected(new Set());
      await fetchRows();
    } catch (e) {
      toast.error(e.message || 'Bulk delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const openResetSingle = (row) => {
    setResetResult(null);
    setResetTargets([{ employee_id: row.employee_id, name: `${row.first_name || ''} ${row.last_name || ''}`.trim(), email: row.email }]);
    setResetOpen(true);
  };

  const openResetBulk = () => {
    setResetResult(null);
    const targets = rows.filter((r) => selected.has(r.employee_id)).map((r) => ({
      employee_id: r.employee_id,
      name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
      email: r.email,
    }));
    if (targets.length === 0) { toast.error('Select rows first'); return; }
    setResetTargets(targets);
    setResetOpen(true);
  };

  const submitReset = async () => {
    if (resetTargets.length === 0) return;
    const isSingle = resetTargets.length === 1;
    setResetting(true);
    try {
      const res = await fetch(`${API}/api/employees/reset-passwords`, {
        method: 'POST',
        headers: jheaders(),
        body: JSON.stringify({
          employee_ids: resetTargets.map((t) => t.employee_id),
          inline: isSingle, // single → JSON, bulk → Excel
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        let detail = `Reset failed (HTTP ${res.status})`;
        try { const j = JSON.parse(text); detail = j.detail || detail; } catch { /* non-json */ }
        throw new Error(detail);
      }

      if (isSingle) {
        // JSON response — display the password inline for HR to copy
        const data = await res.json();
        setResetResult(data);
        toast.success('Password reset — copy the new credentials below');
      } else {
        // Excel blob — download
        const resetCount = res.headers.get('x-reset') || '0';
        const createdCount = res.headers.get('x-created') || '0';
        const skippedNoEmail = res.headers.get('x-skipped-no-email') || '0';
        const errCount = res.headers.get('x-errors') || '0';
        const blob = await res.blob();
        const dl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dl;
        a.download = `password_reset_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(dl);
        toast.success(
          `Passwords ready — ${resetCount} reset, ${createdCount} newly created, ${skippedNoEmail} skipped (no email), ${errCount} error(s)`
        );
        setResetOpen(false);
        setResetTargets([]);
        setSelected(new Set());
      }
    } catch (e) {
      toast.error(e.message || 'Password reset failed');
    } finally {
      setResetting(false);
    }
  };

  const copyResetCred = (cred) => {
    const txt = `Login URL: ${window.location.origin}/login\nEmail: ${cred.email}\nPassword: ${cred.password}`;
    navigator.clipboard?.writeText(txt);
    toast.success('Credentials copied to clipboard');
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.is_active).length;
    const byEntity = {};
    rows.forEach((r) => { const k = r.entity_name || '—'; byEntity[k] = (byEntity[k] || 0) + 1; });
    return { total, active, entities: Object.keys(byEntity).length, byEntity };
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="master-directory-page">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-blue-600" /> Master Employee Directory
          </CardTitle>
          <p className="text-xs text-slate-500">
            Single source of truth across <strong>{stats.entities}</strong> institutes / branches. Every change here auto-syncs to Users, Attendance, Leaves, Payroll &amp; Recruitment.
          </p>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name / email / employee id / designation" data-testid="md-search" />
              </div>
            </div>
            <div className="min-w-[180px]">
              <Label className="text-xs">Institute / Branch</Label>
              <Select value={filterEntity} onValueChange={setFilterEntity}>
                <SelectTrigger data-testid="md-filter-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {meta.entities.map((e) => (
                    <SelectItem key={e.entity_id} value={e.entity_id}>
                      {'\u00A0\u00A0'.repeat(Math.max(0, (e.level || 0)))}{e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs">Department</Label>
              <Select value={filterDept} onValueChange={setFilterDept}>
                <SelectTrigger data-testid="md-filter-dept"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {meta.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs">Designation</Label>
              <Select value={filterDesignation} onValueChange={setFilterDesignation}>
                <SelectTrigger data-testid="md-filter-designation"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All</SelectItem>
                  {(meta.designations || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[150px]">
              <Label className="text-xs">Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger data-testid="md-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {(meta.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs">Appointment</Label>
              <Select value={filterApptType} onValueChange={setFilterApptType}>
                <SelectTrigger data-testid="md-filter-appt"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {(meta.appointment_types || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs">Role</Label>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger data-testid="md-filter-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {meta.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[120px]">
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="md-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchRows} disabled={loading} data-testid="md-refresh">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button onClick={openAdd} data-testid="md-add-employee" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">
              <UserPlus className="h-4 w-4 mr-1.5" /> Add Employee
            </Button>
          </div>

          {/* Bulk action bar */}
          {selected.size > 0 ? (
            <div className="flex items-center gap-2 p-2 mb-2 bg-blue-50 dark:bg-blue-950 border border-blue-200 rounded" data-testid="md-bulk-bar">
              <span className="text-sm font-medium">{selected.size} selected</span>
              <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="md-bulk-edit-btn">
                <Pencil className="h-3 w-3 mr-1" /> Bulk Edit
              </Button>
              <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" onClick={openResetBulk} data-testid="md-bulk-reset-pwd-btn">
                <KeyRound className="h-3 w-3 mr-1" /> Reset Password
              </Button>
              <Button size="sm" variant="outline" className="text-red-700 border-red-300" onClick={() => setBulkDeleteOpen(true)} data-testid="md-bulk-delete-btn">
                <Trash2 className="h-3 w-3 mr-1" /> Bulk Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
            </div>
          ) : null}

          {/* Table */}
          <div className="border rounded-md overflow-x-auto">
            <Table data-testid="md-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="md-select-all" />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin inline-block mr-1" /> Loading…
                  </TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">No employees match the filters</TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.employee_id} data-testid={`md-row-${r.employee_id}`} className={!r.is_active ? 'opacity-60' : ''}>
                    <TableCell>
                      <Checkbox checked={selected.has(r.employee_id)} onCheckedChange={() => toggleOne(r.employee_id)} data-testid={`md-select-${r.employee_id}`} />
                    </TableCell>
                    <TableCell className="font-medium">{r.first_name} {r.last_name}<div className="text-[11px] text-slate-500">{r.employee_id}</div></TableCell>
                    <TableCell className="text-xs">{r.email}</TableCell>
                    <TableCell className="text-xs"><Building2 className="h-3 w-3 inline mr-1 text-slate-400" />{r.entity_name || '—'}</TableCell>
                    <TableCell>{r.department || '—'}</TableCell>
                    <TableCell>{r.designation || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{r.role}</Badge></TableCell>
                    <TableCell>
                      {r.is_active ? <Badge className="bg-emerald-100 text-emerald-800 text-xs">Active</Badge> : <Badge className="bg-slate-200 text-slate-700 text-xs">Inactive</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`md-edit-${r.employee_id}`} title="Edit employee">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-amber-700" onClick={() => openResetSingle(r)} data-testid={`md-reset-pwd-${r.employee_id}`} title="Reset password">
                        <KeyRound className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { setDeleteRow(r); setDeleteHard(false); setDeleteHistory(false); }} data-testid={`md-delete-${r.employee_id}`} title="Delete employee">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Showing {rows.length} of {stats.total} ({stats.active} active)
          </div>
        </CardContent>
      </Card>


      {/* Add Employee dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) { setAddOpen(false); setAddResult(null); } }}>
        <DialogContent className="max-w-xl" data-testid="md-add-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#DC2626]" /> Add New Employee</DialogTitle>
            <DialogDescription>Creates the Employee record + matching User login + seeds leave balances. Returns a temporary password to share.</DialogDescription>
          </DialogHeader>
          {addResult ? (
            <div className="space-y-3" data-testid="md-add-result">
              <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-900 p-4 text-sm">
                <div className="font-semibold text-green-900 dark:text-green-100 mb-2">Employee created — share these credentials once</div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-slate-500">Employee ID</div><div className="col-span-2 font-mono break-all">{addResult.employee_id}</div>
                  <div className="text-slate-500">Email</div><div className="col-span-2 font-mono break-all" data-testid="md-add-result-email">{addResult.email}</div>
                  <div className="text-slate-500">Password</div><div className="col-span-2 font-mono select-all font-bold" data-testid="md-add-result-password">{addResult.temp_password}</div>
                  <div className="text-slate-500">Role</div><div className="col-span-2"><Badge>{addResult.role}</Badge></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={copyAddCreds} data-testid="md-add-copy"><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
                  <Button size="sm" onClick={() => { setAddResult(null); setAddOpen(false); }} data-testid="md-add-done">Done</Button>
                  <Button size="sm" variant="outline" onClick={() => { setAddResult(null); setAddForm((f) => ({ ...f, first_name: '', last_name: '', email: '', phone: '', password: '' })); }} data-testid="md-add-another">Create another</Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First Name *</Label><Input value={addForm.first_name} onChange={(e) => setAddForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="md-add-fname" /></div>
              <div><Label>Last Name</Label><Input value={addForm.last_name} onChange={(e) => setAddForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="md-add-lname" /></div>
              <div><Label>Email *</Label><Input type="email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} data-testid="md-add-email" /></div>
              <div><Label>Phone</Label><Input value={addForm.phone} onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))} data-testid="md-add-phone" /></div>
              <div>
                <Label>Department</Label>
                <Input list="md-add-dept-list" value={addForm.department} onChange={(e) => setAddForm((f) => ({ ...f, department: e.target.value }))} data-testid="md-add-dept" />
                <datalist id="md-add-dept-list">{(meta.departments || []).map((d) => <option key={d} value={d} />)}</datalist>
              </div>
              <div>
                <Label>Designation</Label>
                <Input list="md-add-desig-list" value={addForm.designation} onChange={(e) => setAddForm((f) => ({ ...f, designation: e.target.value }))} data-testid="md-add-desig" />
                <datalist id="md-add-desig-list">{(meta.designations || []).map((d) => <option key={d} value={d} />)}</datalist>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={addForm.category} onValueChange={(v) => setAddForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="md-add-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Teaching', 'Non-Teaching', 'Technical', 'Admin'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    {(meta.categories || []).filter((c) => !['Teaching', 'Non-Teaching', 'Technical', 'Admin'].includes(c)).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Appointment Type</Label>
                <Select value={addForm.appointment_type} onValueChange={(v) => setAddForm((f) => ({ ...f, appointment_type: v }))}>
                  <SelectTrigger data-testid="md-add-appt"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Permanent', 'Contractual', 'Temporary', 'Visiting', 'Probation', 'Ad-hoc', '11-Month Contract'].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Institute / Branch</Label>
                <Select value={addForm.entity_id || 'NONE'} onValueChange={(v) => setAddForm((f) => ({ ...f, entity_id: v === 'NONE' ? '' : v }))}>
                  <SelectTrigger data-testid="md-add-entity"><SelectValue placeholder="— select —" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="NONE">— None —</SelectItem>
                    {(meta.entities || []).map((e) => (
                      <SelectItem key={e.entity_id} value={e.entity_id}>
                        {'\u00A0\u00A0'.repeat(Math.max(0, e.level || 0))}{e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={addForm.role} onValueChange={(v) => setAddForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="md-add-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['employee', 'hod', 'principal', 'director', 'hr', 'finance', 'finance_head', 'branch_admin', 'admin'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Temp Password <span className="text-xs text-slate-400">(auto-generated if blank)</span></Label>
                <Input value={addForm.password} onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))} placeholder="Leave blank to auto-generate" data-testid="md-add-password" />
              </div>
            </div>
          )}
          {!addResult && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={submitAdd} disabled={saving} data-testid="md-add-submit" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />}
                Create Employee
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Single-row edit dialog */}
      <Dialog open={!!editRow} onOpenChange={(o) => { if (!o) setEditRow(null); }}>
        <DialogContent className="max-w-xl" data-testid="md-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
            <DialogDescription>{editRow?.email} — changes will auto-sync to Users, Attendance, Leaves, Payroll, Recruitment.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First Name</Label><Input value={editForm.first_name || ''} onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="md-edit-fname" /></div>
            <div><Label>Last Name</Label><Input value={editForm.last_name || ''} onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="md-edit-lname" /></div>
            <div><Label>Email</Label><Input value={editForm.email || ''} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} data-testid="md-edit-email" /></div>
            <div><Label>Phone</Label><Input value={editForm.phone || ''} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} data-testid="md-edit-phone" /></div>
            <div>
              <Label>Department</Label>
              <Input list="md-edit-dept-options" value={editForm.department || ''} onChange={(e) => setEditForm((f) => ({ ...f, department: e.target.value }))} data-testid="md-edit-dept" />
              <datalist id="md-edit-dept-options">{meta.departments.map((d) => <option key={d} value={d} />)}</datalist>
            </div>
            <div>
              <Label>Designation</Label>
              <Input list="md-edit-desig-options" value={editForm.designation || ''} onChange={(e) => setEditForm((f) => ({ ...f, designation: e.target.value }))} data-testid="md-edit-desig" />
              <datalist id="md-edit-desig-options">{meta.designations.map((d) => <option key={d} value={d} />)}</datalist>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={editForm.category || 'Teaching'} onValueChange={(v) => setEditForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="md-edit-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta.categories.length ? meta.categories : ['Teaching', 'Non-Teaching', 'Technical', 'Admin']).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Appointment Type</Label>
              <Select value={editForm.appointment_type || 'Permanent'} onValueChange={(v) => setEditForm((f) => ({ ...f, appointment_type: v }))}>
                <SelectTrigger data-testid="md-edit-appt"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta.appointment_types.length ? meta.appointment_types : ['Permanent', 'Contract', 'Visiting', 'Probation']).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Institute / Branch</Label>
              <Select value={editForm.entity_id || ''} onValueChange={(v) => setEditForm((f) => ({ ...f, entity_id: v }))}>
                <SelectTrigger data-testid="md-edit-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.entities.map((e) => (
                    <SelectItem key={e.entity_id} value={e.entity_id}>
                      {'\u00A0\u00A0'.repeat(Math.max(0, (e.level || 0)))}{e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={editForm.role || 'employee'} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="md-edit-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta.roles.length ? meta.roles : ['employee', 'hod', 'principal', 'director', 'hr', 'admin', 'branch_admin', 'super_admin']).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Checkbox checked={!!editForm.is_active} onCheckedChange={(v) => setEditForm((f) => ({ ...f, is_active: !!v }))} data-testid="md-edit-active" />
              <Label className="m-0">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button onClick={submitEdit} disabled={saving} data-testid="md-edit-save">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save &amp; Sync Everywhere
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-edit dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg" data-testid="md-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Edit ({selected.size} employees)</DialogTitle>
            <DialogDescription>Leave fields as &ldquo;— keep —&rdquo; to skip. Selected changes apply to every row.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Institute / Branch</Label>
              <Select value={bulkForm.entity_id} onValueChange={(v) => setBulkForm((f) => ({ ...f, entity_id: v }))}>
                <SelectTrigger data-testid="md-bulk-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SENTINEL}>— keep —</SelectItem>
                  {meta.entities.map((e) => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department</Label>
              <Select value={bulkForm.department} onValueChange={(v) => setBulkForm((f) => ({ ...f, department: v }))}>
                <SelectTrigger data-testid="md-bulk-dept"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SENTINEL}>— keep —</SelectItem>
                  {meta.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Designation</Label>
              <Select value={bulkForm.designation} onValueChange={(v) => setBulkForm((f) => ({ ...f, designation: v }))}>
                <SelectTrigger data-testid="md-bulk-desig"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SENTINEL}>— keep —</SelectItem>
                  {meta.designations.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={bulkForm.category} onValueChange={(v) => setBulkForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="md-bulk-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SENTINEL}>— keep —</SelectItem>
                  {(meta.categories.length ? meta.categories : ['Teaching', 'Non-Teaching', 'Technical', 'Admin']).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Appointment Type</Label>
              <Select value={bulkForm.appointment_type} onValueChange={(v) => setBulkForm((f) => ({ ...f, appointment_type: v }))}>
                <SelectTrigger data-testid="md-bulk-appt"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SENTINEL}>— keep —</SelectItem>
                  {(meta.appointment_types.length ? meta.appointment_types : ['Permanent', 'Contract', 'Visiting', 'Probation']).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={bulkForm.role} onValueChange={(v) => setBulkForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="md-bulk-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SENTINEL}>— keep —</SelectItem>
                  {(meta.roles.length ? meta.roles : ['employee', 'hod', 'principal', 'director', 'hr', 'admin', 'branch_admin']).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Active Status</Label>
              <Select value={bulkForm.is_active} onValueChange={(v) => setBulkForm((f) => ({ ...f, is_active: v }))}>
                <SelectTrigger data-testid="md-bulk-active"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SENTINEL}>— keep —</SelectItem>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={submitBulk} disabled={saving} data-testid="md-bulk-save">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Apply to {selected.size}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteRow} onOpenChange={(o) => { if (!o) setDeleteRow(null); }}>
        <DialogContent className="max-w-md" data-testid="md-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Delete Employee</DialogTitle>
            <DialogDescription>{deleteRow?.first_name} {deleteRow?.last_name} · {deleteRow?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <label className="flex items-start gap-2">
              <Checkbox checked={deleteHard} onCheckedChange={(v) => setDeleteHard(!!v)} data-testid="md-delete-hard" />
              <span><strong>Hard delete</strong> (removes user + employee documents permanently). Unchecked = soft delete (just deactivates, reversible).</span>
            </label>
            {deleteHard ? (
              <label className="flex items-start gap-2 pl-6">
                <Checkbox checked={deleteHistory} onCheckedChange={(v) => setDeleteHistory(!!v)} data-testid="md-delete-history" />
                <span>Also delete history — attendance, leaves, leave balances, payroll. <strong className="text-red-700">Cannot be undone.</strong></span>
              </label>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteRow(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitDelete} disabled={deleting} data-testid="md-delete-confirm">
              {deleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              {deleteHard ? (deleteHistory ? 'Delete with history' : 'Delete') : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete dialog */}
      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent className="max-w-md" data-testid="md-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Bulk Delete ({selected.size} employees)</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <label className="flex items-start gap-2">
              <Checkbox checked={bulkDeleteHard} onCheckedChange={(v) => setBulkDeleteHard(!!v)} data-testid="md-bulk-delete-hard" />
              <span><strong>Hard delete</strong> all {selected.size} (irreversible). Unchecked = soft delete (deactivate).</span>
            </label>
            {bulkDeleteHard ? (
              <label className="flex items-start gap-2 pl-6">
                <Checkbox checked={bulkDeleteHistory} onCheckedChange={(v) => setBulkDeleteHistory(!!v)} data-testid="md-bulk-delete-history" />
                <span>Also delete history (attendance, leaves, payroll). <strong className="text-red-700">Cannot be undone.</strong></span>
              </label>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={submitBulkDelete} disabled={deleting} data-testid="md-bulk-delete-confirm">
              {deleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              {bulkDeleteHard ? 'Delete all' : 'Deactivate all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset dialog — handles both single (inline JSON) and bulk (Excel download) */}
      <Dialog open={resetOpen} onOpenChange={(o) => { if (!o && !resetting) { setResetOpen(false); setResetTargets([]); setResetResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="md-reset-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <KeyRound className="h-5 w-5" />
              {resetResult ? 'New Credentials' : `Reset Password${resetTargets.length > 1 ? `s (${resetTargets.length} employees)` : ''}`}
            </DialogTitle>
            {!resetResult && (
              <DialogDescription>
                A strong 10-character password will be generated for {resetTargets.length === 1 ? 'this employee' : 'each selected employee'}.
                The affected user{resetTargets.length > 1 ? 's' : ''} will be flagged to change password on next login.
                {resetTargets.length === 1
                  ? " You'll see the new password on this screen with a Copy button."
                  : " You'll receive an Excel file with all new credentials."}
              </DialogDescription>
            )}
          </DialogHeader>

          {resetResult ? (
            // Inline result view (single reset only)
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-emerald-900 text-sm">
                Password reset successful. Share the credentials below with the employee — this password is shown <strong>only once</strong>.
              </div>
              {(resetResult.credentials || []).map((cred) => (
                <div key={cred.employee_id} className="border rounded-md p-3 space-y-2 bg-slate-50 dark:bg-slate-900" data-testid={`md-reset-result-${cred.employee_id}`}>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-slate-500">Name</div>
                    <div className="col-span-2 font-medium">{cred.name || cred.employee_id}</div>
                    <div className="text-slate-500">Email (Username)</div>
                    <div className="col-span-2 font-mono text-xs select-all break-all">{cred.email}</div>
                    <div className="text-slate-500">Password</div>
                    <div className="col-span-2 font-mono select-all font-bold text-base tracking-wide" data-testid={`md-reset-result-password-${cred.employee_id}`}>{cred.password}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => copyResetCred(cred)} className="w-full" data-testid={`md-reset-copy-${cred.employee_id}`}>
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Login + Email + Password
                  </Button>
                </div>
              ))}
              {resetResult.errors && resetResult.errors.length > 0 && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {resetResult.errors.length} error(s): {resetResult.errors.map((e) => e.error).join(', ')}
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => { setResetOpen(false); setResetTargets([]); setResetResult(null); }} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="md-reset-done">
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // Pre-confirm view (list targets + confirm button)
            <>
              <div className="space-y-2 text-sm">
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-900 flex items-start gap-2">
                  <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>This overwrites existing passwords.</strong> Only proceed if you intend to reset.
                    The affected user{resetTargets.length > 1 ? 's' : ''} will not be able to login with their old password.
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {resetTargets.map((t) => (
                        <TableRow key={t.employee_id}>
                          <TableCell className="text-xs py-1.5">{t.name || t.employee_id}</TableCell>
                          <TableCell className="text-xs py-1.5">{t.email || <span className="text-red-500">(no email — will be skipped)</span>}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setResetOpen(false); setResetTargets([]); }} disabled={resetting}>Cancel</Button>
                <Button
                  onClick={submitReset}
                  disabled={resetting || resetTargets.length === 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="md-reset-pwd-confirm"
                >
                  {resetting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1" />}
                  {resetTargets.length === 1 ? 'Reset Password' : 'Reset & Download Excel'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
